// Draw 模式评分:把玩家笔迹与真实轮廓做双向 Chamfer 比对。
// 设计要点见 docs/geodle-draw-mode-plan.md §3。

export type Pt = [number, number];
export type Stroke = Pt[];
export type Ring = number[][];

export interface Ray {
  p: Pt; // 玩家采样点(已贴合到目标框)
  q: Pt; // 真轮廓上的最近点
  d: number; // 偏差像素
  out: boolean; // true=画到形状外面,false=切进里面
}

export interface DrawResult {
  score: number; // 0-100,越高越好
  accuracy: number; // 0-100:你的线贴不贴边
  coverage: number; // 0-100:形状覆盖全不全
  deviation: number; // 平均偏差(百分比,越低越好)
  rays: Ray[];
  fitted: Stroke[]; // 贴合后的笔迹(用于结果图叠加)
}

/** ≥ 此偏差得 0 分。约 2% 偏差≈91,11%≈50。 */
const TOL = 0.22;
const SAMPLE_STEP = 7; // 重采样间距(px)

const bboxOf = (pts: Pt[] | number[][]): [number, number, number, number] =>
  pts.reduce<[number, number, number, number]>(
    (b, p) => [Math.min(b[0], p[0]), Math.min(b[1], p[1]), Math.max(b[2], p[0]), Math.max(b[3], p[1])],
    [Infinity, Infinity, -Infinity, -Infinity],
  );

/** 沿折线等距重采样,消除画速造成的采样密度差异 */
function resample(pts: number[][], step: number): Pt[] {
  if (pts.length < 2) return pts.map((p) => [p[0], p[1]] as Pt);
  const out: Pt[] = [[pts[0][0], pts[0][1]]];
  let acc = 0;
  for (let i = 1; i < pts.length; i++) {
    let px = pts[i - 1][0];
    let py = pts[i - 1][1];
    const cx = pts[i][0];
    const cy = pts[i][1];
    let seg = Math.hypot(cx - px, cy - py);
    while (acc + seg >= step) {
      const t = (step - acc) / seg;
      px += (cx - px) * t;
      py += (cy - py) * t;
      out.push([px, py]);
      seg = Math.hypot(cx - px, cy - py);
      acc = 0;
    }
    acc += seg;
  }
  return out;
}

/** 点到线段的距离与最近点 */
function distToSeg(p: Pt, a: number[], b: number[]): [number, Pt] {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const L = vx * vx + vy * vy;
  let t = L > 0 ? ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / L : 0;
  t = Math.max(0, Math.min(1, t));
  const q: Pt = [a[0] + vx * t, a[1] + vy * t];
  return [Math.hypot(p[0] - q[0], p[1] - q[1]), q];
}

function nearestOnRings(p: Pt, rings: Ring[]): [number, Pt] {
  let bd = Infinity;
  let bq: Pt = p;
  for (const r of rings)
    for (let i = 1; i < r.length; i++) {
      const [d, q] = distToSeg(p, r[i - 1], r[i]);
      if (d < bd) {
        bd = d;
        bq = q;
      }
    }
  return [bd, bq];
}

function nearestOnStrokes(p: Pt, strokes: Stroke[]): number {
  let bd = Infinity;
  for (const s of strokes) {
    if (s.length < 2) {
      if (s.length) bd = Math.min(bd, Math.hypot(p[0] - s[0][0], p[1] - s[0][1]));
      continue;
    }
    for (let i = 1; i < s.length; i++) {
      const [d] = distToSeg(p, s[i - 1], s[i]);
      if (d < bd) bd = d;
    }
  }
  return bd;
}

/** even-odd 射线法:点是否落在形状内部 */
function isInside(p: Pt, rings: Ring[]): boolean {
  let c = false;
  for (const r of rings)
    for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
      const [xi, yi] = r[i];
      const [xj, yj] = r[j];
      if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi || 1e-9) + xi) c = !c;
    }
  return c;
}

const EMPTY: DrawResult = { score: 0, accuracy: 0, coverage: 0, deviation: 100, rays: [], fitted: [] };

/**
 * 评分。strokes/rings 必须在同一坐标系(同一画布尺寸)。
 * 形状优先:先把笔迹等比贴合到目标包围盒,所以落笔位置与大小不影响得分。
 */
export function scoreDrawing(strokes: Stroke[], rings: Ring[]): DrawResult {
  const drawn = strokes.filter((s) => s.length > 1);
  if (!drawn.length || !rings.length) return EMPTY;

  const ub = bboxOf(drawn.flat());
  const tb = bboxOf(rings.flat());
  const uw = ub[2] - ub[0] || 1;
  const uh = ub[3] - ub[1] || 1;
  const tw = tb[2] - tb[0] || 1;
  const th = tb[3] - tb[1] || 1;

  const k = Math.min(tw / uw, th / uh);
  const ucx = (ub[0] + ub[2]) / 2;
  const ucy = (ub[1] + ub[3]) / 2;
  const tcx = (tb[0] + tb[2]) / 2;
  const tcy = (tb[1] + tb[3]) / 2;
  const fitted: Stroke[] = drawn.map((s) =>
    s.map(([x, y]) => [(x - ucx) * k + tcx, (y - ucy) * k + tcy] as Pt),
  );

  const diag = Math.hypot(tw, th);
  const userPts = fitted.flatMap((s) => resample(s, SAMPLE_STEP));
  const targetPts = rings.flatMap((r) => resample(r, SAMPLE_STEP));

  // accuracy:你的点 → 真轮廓
  let accSum = 0;
  const rays: Ray[] = [];
  for (const p of userPts) {
    const [d, q] = nearestOnRings(p, rings);
    accSum += d;
    rays.push({ p, q, d, out: !isInside(p, rings) });
  }
  // coverage:真轮廓 → 你的线(堵住「只画一小段也高分」)
  let covSum = 0;
  for (const p of targetPts) covSum += nearestOnStrokes(p, fitted);

  const accuracy = accSum / userPts.length;
  const coverage = covSum / targetPts.length;
  const deviation = (accuracy + coverage) / 2 / diag;
  const pct = (v: number) => Math.max(0, Math.min(100, Math.round(100 * (1 - v / diag / TOL))));

  return {
    score: Math.max(0, Math.min(100, Math.round(100 * (1 - deviation / TOL)))),
    accuracy: pct(accuracy),
    coverage: pct(coverage),
    deviation: Math.round(deviation * 1000) / 10,
    rays,
    fitted,
  };
}

/** 形状越复杂给的时间越多(周长 + 岛屿数),20–45 秒 */
export function secondsFor(rings: Ring[], canvasSize: number): number {
  let perim = 0;
  for (const r of rings)
    for (let i = 1; i < r.length; i++) perim += Math.hypot(r[i][0] - r[i - 1][0], r[i][1] - r[i - 1][1]);
  return Math.round(Math.max(20, Math.min(45, 16 + (perim / canvasSize) * 2.2 + rings.length * 0.8)));
}

/** 分数 → 等级序号(0=最高),对应 i18n draw.ranks */
export const RANK_CUTS = [90, 75, 60, 42, 25, 10, 0];
export const rankIndexFor = (score: number): number =>
  Math.max(0, RANK_CUTS.findIndex((c) => score >= c));
